import { useState } from 'react';
import { useReglages } from '../../api/reglages.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { DEVISE } from '../../lib/currency';
import { C } from '../../theme/palette';

/** Clé du réglage de société qui porte les modèles. */
export const CLE_MODELES = 'packsChantier';

/**
 * Modèles de chantier.
 *
 * Un artisan refait souvent le même ouvrage — une salle de bain, une façade —
 * avec les mêmes prestations et des quantités qui varient. Le modèle fige la
 * liste ; le devis la charge et n'a plus qu'à ajuster.
 *
 * Les modèles vivent dans les paramètres de la société, donc en base : ils
 * valent pour toute l'entreprise et survivent au changement d'appareil. Ils
 * n'ont pas de table à eux parce qu'ils ne sont jamais interrogés autrement
 * que tous ensemble.
 */
export function ModelesPage({ onNav, soc, catalogue, estMixte, univers, setUnivers }) {
  const { reglageJson, setReglageJson } = useReglages();
  const models = reglageJson(CLE_MODELES, []);

  const [edit, setEdit] = useState(null);
  const [nom, setNom] = useState('');
  const [lignes, setLignes] = useState([]);
  const [showCat, setShowCat] = useState(false);
  const [rech, setRech] = useState('');

  const ouvrir = (i) => {
    if (i === 'new') {
      setNom('');
      setLignes([]);
    } else {
      setNom(models[i].nom);
      setLignes((models[i].lignes || []).map((l) => ({ ...l })));
    }
    setShowCat(false);
    setRech('');
    setEdit(i);
  };

  const enregistrer = () => {
    if (!nom.trim()) {
      window.alert('Donne un nom au modèle.');
      return;
    }
    if (!lignes.length) {
      window.alert('Ajoute au moins une prestation.');
      return;
    }
    const m = { nom: nom.trim(), lignes };
    const arr = [...models];
    if (edit === 'new') {
      arr.push(m);
    } else {
      arr[edit] = m;
    }
    setReglageJson(CLE_MODELES, arr);
    setEdit(null);
    window.alert('✓ Modèle « ' + m.nom + ' » enregistré.');
  };

  const supprimer = (i) => {
    if (!window.confirm('Supprimer ce modèle ?')) {
      return;
    }
    setReglageJson(
      CLE_MODELES,
      models.filter((_, j) => j !== i),
    );
  };

  const majLigne = (j, champ, v) =>
    setLignes((p) => p.map((l, k) => (k === j ? { ...l, [champ]: v } : l)));
  const supprLigne = (j) => setLignes((p) => p.filter((_, k) => k !== j));
  const ajouter = (a) => {
    setLignes((p) => [...p, { d: a.nom, sd: a.sd || '', u: a.u, q: 1, p: a.p, rem: 0, ref: a.id }]);
    setShowCat(false);
    setRech('');
  };

  const cat = (catalogue || []).filter(
    (a) => !rech.trim() || (a.nom || '').toLowerCase().includes(rech.toLowerCase()),
  );

  if (edit !== null) {
    return (
      <Shell
        active="plus"
        onNav={onNav}
        soc={soc}
        back
        onBack={() => setEdit(null)}
        pageTitle="✏️ Modèle de chantier"
        estMixte={estMixte}
        univers={univers}
        setUnivers={setUnivers}
      >
        <Card>
          <div style={{ padding: 13 }}>
            <Inp
              label="Nom du modèle"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Ex : Rénovation salle de bain"
            />
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: C.muted,
                textTransform: 'uppercase',
                margin: '12px 0 7px',
              }}
            >
              Prestations ({lignes.length})
            </div>
            {lignes.length === 0 && (
              <div style={{ fontSize: 10.5, color: C.muted, textAlign: 'center', padding: 10 }}>
                Aucune prestation. Ajoute-les depuis ton catalogue ci-dessous.
              </div>
            )}
            {lignes.map((l, j) => (
              <div
                key={j}
                style={{
                  background: C.bg,
                  border: '1px solid ' + C.border,
                  borderRadius: 9,
                  padding: 10,
                  marginBottom: 7,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 7,
                    gap: 8,
                  }}
                >
                  <input
                    value={l.d}
                    onChange={(e) => majLigne(j, 'd', e.target.value)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      fontSize: 12,
                      fontWeight: 700,
                      flex: 1,
                      color: C.ink,
                      outline: 'none',
                    }}
                  />
                  <span
                    onClick={() => supprLigne(j)}
                    style={{ color: C.red, cursor: 'pointer', fontSize: 16, flexShrink: 0 }}
                  >
                    ×
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    ['QUANTITÉ (' + (l.u || 'u') + ')', 'q', l.q],
                    ['PRIX UNITAIRE', 'p', l.p],
                  ].map(([lb, champ, val]) => (
                    <div key={champ} style={{ flex: 1 }}>
                      <div
                        style={{ fontSize: 8, color: C.muted, marginBottom: 2, fontWeight: 700 }}
                      >
                        {lb}
                      </div>
                      <input
                        type="number"
                        value={val}
                        onChange={(e) => majLigne(j, champ, Number(e.target.value))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 7,
                          padding: 8,
                          fontSize: 13,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {showCat && (
              <div
                style={{
                  border: '1px solid ' + C.teal,
                  borderRadius: 9,
                  padding: 8,
                  marginBottom: 8,
                }}
              >
                <input
                  value={rech}
                  onChange={(e) => setRech(e.target.value)}
                  placeholder="🔍 Chercher un article…"
                  autoFocus
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    border: '1px solid ' + C.border,
                    borderRadius: 7,
                    padding: 8,
                    fontSize: 12,
                    marginBottom: 6,
                  }}
                />
                <div style={{ maxHeight: 220, overflow: 'auto' }}>
                  {cat.length === 0 ? (
                    <div style={{ fontSize: 10, color: C.muted, padding: 8, textAlign: 'center' }}>
                      Aucun article. Ajoute-les dans ton catalogue (Offre).
                    </div>
                  ) : (
                    cat.map((a) => (
                      <div
                        key={a.id}
                        onClick={() => ajouter(a)}
                        style={{
                          padding: '9px 6px',
                          borderBottom: '1px solid ' + C.border,
                          cursor: 'pointer',
                          fontSize: 11.5,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span>
                          {a.nom} <span style={{ fontSize: 8, color: C.muted }}>({a.u})</span>
                        </span>
                        <b style={{ color: C.teal }}>{DEVISE + ' ' + a.p}</b>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div
              onClick={() => {
                setShowCat((s) => !s);
                setRech('');
              }}
              style={{
                textAlign: 'center',
                border: '1.5px dashed ' + C.teal,
                borderRadius: 9,
                padding: 11,
                color: C.teal,
                fontWeight: 800,
                fontSize: 12,
                marginBottom: 8,
                cursor: 'pointer',
              }}
            >
              {showCat ? 'Fermer le catalogue' : '+ Ajouter une prestation (depuis le catalogue)'}
            </div>
            <Btn label="💾 Enregistrer le modèle" color={C.green} full onClick={enregistrer} />
          </div>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      back
      onBack={() => onNav('plus')}
      pageTitle="📦 Modèles de chantier"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div
        onClick={() => ouvrir('new')}
        style={{
          background: C.green,
          color: '#fff',
          borderRadius: 11,
          padding: 14,
          textAlign: 'center',
          fontWeight: 800,
          fontSize: 14,
          marginBottom: 14,
          cursor: 'pointer',
        }}
      >
        + Créer un modèle
      </div>
      <div
        style={{
          fontSize: 10,
          color: C.grey,
          marginBottom: 10,
          lineHeight: 1.5,
          padding: '0 4px',
        }}
      >
        Prépare tes chantiers types (salle de bain, cuisine, façade…). Ensuite, dans un devis, tu
        les charges d&apos;un clic.
      </div>
      {models.length === 0 ? (
        <Card>
          <div style={{ padding: 18, textAlign: 'center', color: C.muted, fontSize: 11 }}>
            Aucun modèle pour l&apos;instant. Crée ton premier chantier type ! 👆
          </div>
        </Card>
      ) : (
        models.map((m, i) => (
          <Card key={i} style={{ marginBottom: 10 }}>
            <div
              style={{
                padding: 13,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800 }}>📦 {m.nom}</div>
                <div style={{ fontSize: 9.5, color: C.muted, marginTop: 2 }}>
                  {(m.lignes || []).length} prestation
                  {(m.lignes || []).length > 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <span
                  onClick={() => ouvrir(i)}
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    padding: '8px 12px',
                    borderRadius: 8,
                    background: C.tealL,
                    color: C.teal,
                    cursor: 'pointer',
                  }}
                >
                  ✏️ Modifier
                </span>
                <span
                  onClick={() => supprimer(i)}
                  style={{
                    fontSize: 14,
                    padding: '7px 10px',
                    borderRadius: 8,
                    background: C.redL,
                    color: C.red,
                    cursor: 'pointer',
                  }}
                >
                  🗑️
                </span>
              </div>
            </div>
          </Card>
        ))
      )}
    </Shell>
  );
}
